import { LenderPackageItemResponse, PackageItemState } from '../types/lender-packages.types';
import { AUTO_ENABLED_ITEM_TYPES, INSTALLATION_METHODS, InstallationMethod } from '../constants/lender-packages.constants';

export interface PackageMapperInput {
  totalModules: number;
  totalInverters: number;
  totalBatteries: number;
  totalOptimizersRsd: number;
  totalMicroinverters: number;
  totalMciS: number;
  installationMethod: InstallationMethod;
  estimatedAttachments: number;
  /** BOM calculated/set fields — source of truth from Redux lgcy_results */
  bomResults: {
    sumOfPanels: number;
    railsInTotal: number;
    spliceKit: number;
    endCap: number;
    midClamps: number;
    endClamps: number;
    groundingLugs: number;
    wireClamp: number;
    wireRetentionClip: number;
  };
}

/** bom_kind values that use estimated_attachments as quantity */
const ATTACHMENT_EST_KINDS = ['kit', 'chem-curb'];

/** bom_kind values that use installation method formula */
const ATTACHMENT_FASTENER_KINDS = ['lag-bolt', 'fastener'];

/** bom_kind → BOM calculated field mapping for racking items */
const RACKING_BOM_KIND_MAP: Record<string, keyof PackageMapperInput['bomResults']> = {
  'rail': 'railsInTotal',
  'splice': 'spliceKit',
  'rail-end-cap': 'endCap',
  'mid-clamp': 'midClamps',
  'end-clamp': 'endClamps',
  'ground-lug': 'groundingLugs',
  'rail-wire-clamp': 'wireClamp',
  'rail-wire-clip': 'wireRetentionClip',
};

/**
 * Determines the bom_item quantity for a given item_type based on equipment data.
 */
const getQuantityByItemType = (itemType: string, input: PackageMapperInput): number => {
  const type = itemType.toLowerCase();
  if (type === 'pv-module') return input.bomResults.sumOfPanels || input.totalModules;
  if (type === 'inverter') return input.totalInverters;
  if (type === 'storage-unit') return input.totalBatteries;
  if (type === 'optimizer-rsd') return input.totalOptimizersRsd;
  return 0;
};

/**
 * Determines bom_item quantity for attachment sub-items based on bom_kind.
 * - kit, chem-curb → estimated_attachments
 * - lag-bolt, fastener → rafter_mount: 2, deck_mount: estimated_attachments*2
 */
const getAttachmentBomQty = (bomKind: string, input: PackageMapperInput): number => {
  const kind = bomKind.toLowerCase();
  if (ATTACHMENT_EST_KINDS.includes(kind)) return input.estimatedAttachments;
  if (ATTACHMENT_FASTENER_KINDS.includes(kind)) return input.installationMethod === INSTALLATION_METHODS.DECK_MOUNT ? input.estimatedAttachments * 4 : input.estimatedAttachments * 2;
  return 0;
};

/**
 * Determines bom_item quantity for racking sub-items based on bom_kind.
 * Maps bom_kind to the corresponding BOM calculated field.
 */
const getRackingBomQty = (bomKind: string, input: PackageMapperInput): number => {
  const kind = bomKind.toLowerCase();
  if (kind === 'mlpe-mount') return input.totalMicroinverters + input.totalMciS;
  const resultKey = RACKING_BOM_KIND_MAP[kind];
  if (resultKey) return input.bomResults[resultKey];
  return 0;
};

/**
 * Determines if an item_type should be auto-enabled (checked) by default.
 */
const isAutoEnabled = (itemType: string, input: PackageMapperInput): boolean => {
  const type = itemType.toLowerCase();
  if ((AUTO_ENABLED_ITEM_TYPES as readonly string[]).includes(type)) return true;
  if (type === 'storage-unit') return input.totalBatteries > 0;
  if (type === 'optimizer-rsd') return input.totalOptimizersRsd > 0;
  return false;
};

/**
 * Resolves bom_item quantity based on parent item_type and bom_kind.
 */
const resolveBomItemQty = (itemType: string, bomKind: string, input: PackageMapperInput): number => {
  const type = itemType.toLowerCase();
  if (type === 'attachment') return getAttachmentBomQty(bomKind, input);
  if (type === 'racking') return getRackingBomQty(bomKind, input);
  return getQuantityByItemType(type, input);
};

/**
 * Updates bom_item quantities on existing items based on current BOM/equipment values.
 * Preserves enabled state — only changes quantities.
 */
export const updatePackageItemQuantities = (
  items: PackageItemState[],
  input: PackageMapperInput,
): PackageItemState[] => {
  return items.map(item => {
    const type = item.item_type.toLowerCase();
    return {
      ...item,
      bom_items: item.bom_items.map(bom => ({
        ...bom,
        quantity: resolveBomItemQty(type, bom.bom_kind, input),
      })),
    };
  });
};

/**
 * Maps API package items to Redux-ready PackageItemState[].
 * Auto-populates quantities from equipment/BOM data and sets enabled state by item_type.
 */
export const mapPackageItems = (
  apiItems: LenderPackageItemResponse[],
  input: PackageMapperInput,
): PackageItemState[] => {
  return apiItems.map(item => {
    const type = item.item_type.toLowerCase();
    const enabled = isAutoEnabled(type, input);

    return {
      id: item.id,
      item_type: item.item_type,
      equipment_label: item.equipment_label,
      mpn: item.mpn,
      make: item.make,
      quantity: 1,
      enabled,
      bom_items: item.bom_items
        .filter(bom => bom.bom_sku && bom.bom_sku.toLowerCase() !== 'n/a')
        .map(bom => ({
          ...bom,
          enabled,
          quantity: resolveBomItemQty(type, bom.bom_kind, input),
        })),
    };
  });
};
